import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { fmtEUR, fmtTL } from "../lib/api";
import { FileText, CheckCircle2, XCircle, Clock, Euro, Wallet, Mail } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";

const Stat = ({ label, value, sub, icon: Icon, tone = "default", testid, onClick }) => {
  const tones = {
    default: "text-zinc-950",
    green: "text-[#00913c]",
    red: "text-[#b71c1c]",
    amber: "text-[#8a5b00]",
  };
  return (
    <div className={`grid-card p-6 ${onClick ? "cursor-pointer hover:border-zinc-950 transition-colors" : ""}`} data-testid={testid} onClick={onClick}>
      <div className="flex items-start justify-between">
        <div className="label-overline">{label}</div>
        <Icon size={16} className="text-zinc-400" />
      </div>
      <div className={`mt-4 font-display font-black text-4xl tracking-tight ${tones[tone]}`}>{value}</div>
      {sub && <div className="mt-2 text-xs text-zinc-500">{sub}</div>}
      {onClick && <div className="mt-3 text-[11px] label-overline text-[#0033cc]">Detay →</div>}
    </div>
  );
};

export default function Dashboard() {
  const [s, setS] = useState(null);
  const [recent, setRecent] = useState([]);
  const [sending, setSending] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/stats").then((r) => setS(r.data));
    api.get("/cases").then((r) => setRecent(r.data.slice(0, 6)));
  }, []);

  const sendMonthly = async () => {
    setSending(true);
    try {
      const r = await api.post("/reports/monthly/send");
      toast.success(`Rapor gönderildi: ${r.data.sent_to}`);
    } catch { toast.error("Rapor gönderilemedi"); }
    finally { setSending(false); }
  };

  return (
    <div className="p-6 md:p-8 max-w-[1400px]" data-testid="dashboard-page">
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <div className="label-overline">Genel Bakış</div>
          <h1 className="mt-2 font-display font-black text-3xl md:text-4xl tracking-tight">Panel</h1>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={sendMonthly} disabled={sending} className="h-10 px-4 text-sm border border-zinc-200 rounded-md hover:bg-zinc-100 flex items-center gap-2 disabled:opacity-50" data-testid="send-monthly-report-btn">
            <Mail size={14} /> {sending ? "Gönderiliyor…" : "Aylık Rapor Gönder"}
          </button>
          <div className="text-xs text-zinc-500">{new Date().toLocaleDateString("tr-TR", { day: "2-digit", month: "long", year: "numeric" })}</div>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat testid="stat-total" label="Toplam Başvuru" value={s?.total ?? 0} icon={FileText} onClick={() => navigate("/cases")} />
        <Stat testid="stat-approved" label="Onaylanan" value={s?.approved ?? 0} sub={`Onay oranı %${s?.approval_rate ?? 0}`} icon={CheckCircle2} tone="green" onClick={() => navigate("/cases")} />
        <Stat testid="stat-rejected" label="Red" value={s?.rejected ?? 0} icon={XCircle} tone="red" onClick={() => navigate("/cases")} />
        <Stat testid="stat-pending" label="Bekleyen" value={s?.pending ?? 0} icon={Clock} tone="amber" onClick={() => navigate("/cases")} />
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
        <Stat testid="stat-cust-eur" label="Müşteri Tahsilat (EUR)" value={fmtEUR(s?.customer_paid_eur)} icon={Euro} onClick={() => navigate("/payments-all")} />
        <Stat testid="stat-cust-tl" label="Müşteri Tahsilat (TL)" value={fmtTL(s?.customer_paid_tl)} icon={Wallet} onClick={() => navigate("/payments-all")} />
        <Stat testid="stat-supp-bal" label="Tedarikçi Bakiye (EUR)" value={fmtEUR(s?.supplier_balance_eur)} sub={`Borç: ${fmtEUR(s?.supplier_cost_total_eur)} · Ödenen: ${fmtEUR(s?.supplier_paid_eur)}`} icon={Building} tone={s?.supplier_balance_eur > 0 ? "red" : "green"} onClick={() => navigate("/suppliers")} />
      </div>

      <div className="mt-10">
        <div className="flex items-baseline justify-between">
          <h2 className="font-display font-bold text-xl">Ülkelere Göre</h2>
          <Link to="/stats" className="text-xs text-zinc-500 hover:text-zinc-950 label-overline">Detay →</Link>
        </div>
        <div className="mt-4 grid-card overflow-x-auto">
          <table className="w-full text-sm min-w-[600px]">
            <thead className="bg-zinc-50 text-left">
              <tr className="border-b border-zinc-200">
                <Th>Ülke</Th><Th>Toplam</Th><Th>Onay</Th><Th>Red</Th><Th>Onay %</Th>
              </tr>
            </thead>
            <tbody>
              {(s?.by_country || []).slice(0, 8).map((c) => (
                <tr key={c.country_id} className="border-b border-zinc-100 hover:bg-zinc-50">
                  <Td className="font-medium">{c.country_name}</Td>
                  <Td>{c.total}</Td>
                  <Td className="text-[#00913c]">{c.approved}</Td>
                  <Td className="text-[#b71c1c]">{c.rejected}</Td>
                  <Td>%{c.approval_rate}</Td>
                </tr>
              ))}
              {(!s?.by_country || s.by_country.length === 0) && (
                <tr><td colSpan={5} className="p-8 text-center text-sm text-zinc-500">Henüz veri yok</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-10">
        <div className="flex items-baseline justify-between">
          <h2 className="font-display font-bold text-xl">Son İşlemler</h2>
          <Link to="/cases" className="text-xs text-zinc-500 hover:text-zinc-950 label-overline">Tümü →</Link>
        </div>
        <div className="mt-4 grid-card overflow-x-auto">
          <table className="w-full text-sm min-w-[700px]">
            <thead className="bg-zinc-50 text-left">
              <tr className="border-b border-zinc-200">
                <Th>Ad Soyad</Th><Th>Ülke</Th><Th>Tedarikçi</Th><Th>Kalan</Th><Th>Durum</Th>
              </tr>
            </thead>
            <tbody>
              {recent.map((c) => (
                <tr key={c.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                  <Td className="font-medium">{c.full_name}</Td>
                  <Td>{c.country_name}</Td>
                  <Td>{c.supplier_name || "—"}</Td>
                  <Td>{c.remaining_currency === "TL" ? fmtTL(c.remaining) : fmtEUR(c.remaining)}</Td>
                  <Td><StatusPill v={c.status} /></Td>
                </tr>
              ))}
              {recent.length === 0 && (
                <tr><td colSpan={5} className="p-8 text-center text-sm text-zinc-500">
                  <Link to="/cases" className="underline">İlk vize kaydınızı ekleyin →</Link>
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const Th = ({ children }) => <th className="px-4 py-3 label-overline font-medium">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;

const Building = ({ size = 16, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}><rect x="4" y="2" width="16" height="20" rx="1"/><path d="M8 6h.01M12 6h.01M16 6h.01M8 10h.01M12 10h.01M16 10h.01M8 14h.01M12 14h.01M16 14h.01M10 22v-4h4v4"/></svg>
);

import { STATUS_OPTIONS } from "../lib/api";
const StatusPill = ({ v }) => {
  const s = STATUS_OPTIONS.find((x) => x.value === v);
  return <span className={`status-pill ${s?.pill || "status-appt"}`}>{s?.label || v}</span>;
};
